import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { QueryObserverResult, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";

import { UserInfo } from "@/api/rest/models/account";
import { HouseholdData } from "@/api/rest/models/household";
import { getAllQuestions } from "@/api/rest/services/questionService";
import LoadingLogo from "@/components/common/LoadingLogo";
import ListQuestions from "@/components/question-contents/ListQuestions";

export default function QuestionListPage() {
  const navigate = useNavigate();
  const { householdId } = useParams<{ householdId: string }>();
  const { t, i18n } = useTranslation();

  const { user, currentHousehold } = useOutletContext<{
    user: UserInfo;
    currentHousehold: {
      isLoading: boolean;
      data: {
        result: HouseholdData | undefined;
      };
      refetch: () => Promise<QueryObserverResult>;
    };
  }>();

  const { data: questions, isLoading: isLoadingQuestions } = useQuery({
    queryKey: ["questions", householdId, i18n.language],
    queryFn: async () => {
      if (!householdId) {
        return null;
      }
      const response = await getAllQuestions(Number(householdId));
      if (!response.success) {
        throw new Error(response.message || "Failed to fetch questions");
      }
      return response.result;
    },
    enabled: Boolean(householdId),
  });

  const householdData = currentHousehold.data?.result;
  const loading = currentHousehold.isLoading || isLoadingQuestions;

  if (loading) {
    return <LoadingLogo fullPage size="lg" />;
  }

  if (!householdData) {
    return <div>Household not found</div>;
  }

  return (
    <main className="flex flex-grow flex-col p-4 md:p-12 md:overflow-y-auto scrollbar-thin scrollbar-track-slate-100 scrollbar-thumb-white items-center">
      <div className="flex flex-grow self-stretch flex-col">
        <div className="flex flex-row ">
          <ChevronLeftIcon
            className="h-9 w-9 mr-2 hover:bg-slate-300 focus-visible:bg-dobu-orange cursor-pointer rounded"
            onClick={() => navigate(-1)}
          />
          <h2 className="text-3xl font-bold text-center mr-4">{t("question.questions")}</h2>
        </div>
        <div className="flex flex-col lg:flex-row gap-8 mt-8">
          <div className="flex flex-grow basis-1/2 flex-col gap-4 items-stretch">
            {(questions ?? []).length > 0 ? (
              <ListQuestions questions={questions ?? []} user={user} building={householdData?.building} />
            ) : (
              <div className="prose prose-sm prose-gray text-gray-500">
                <p>{t("question.no_questions_yet")}</p>
                <p>{t("question.ask_in_chat_hint")}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
